import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Menu, X, Globe } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

export default function Layout({ children }) {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const links = [
    { to: "/framework", label: t.nav.framework },
    { to: "/assessment", label: t.nav.assessment },
    { to: "/mediation", label: t.nav.mediation },
    { to: "/agreement", label: t.nav.agreement },
    { to: "/toolkit", label: t.nav.toolkit },
    { to: "/pilot", label: t.nav.pilot },
    { to: "/book", label: t.nav.book },
    { to: "/research", label: t.nav.research },
    { to: "/about", label: t.nav.about },
    { to: "/developer", label: t.nav.developer },
  ];

  const setLanguage = (code) => { setLang(code); setOpen(false); };

  return (
    <div className="min-h-screen bg-[#050914] text-slate-100">
      <header data-testid="mift-header"
              className="sticky top-0 z-50 backdrop-blur-xl bg-[#050914]/80 border-b border-slate-800/80">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-2 group">
            <span className="inline-block w-2.5 h-2.5 bg-blue-500 rounded-full shadow-[0_0_20px_rgba(59,130,246,0.6)]" />
            <span className="font-semibold tracking-tight text-lg">MIFT</span>
            <span className="hidden md:inline text-xs text-slate-400 border-l border-slate-700 pl-2 ml-1 max-w-[20rem] truncate">
              Integrated Mediation Methodology
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to}
                       data-testid={`nav-${l.to.replace("/", "")}`}
                       className={({ isActive }) =>
                         `px-3 py-2 text-sm rounded-full transition-colors ${
                           isActive ? "text-blue-300 bg-blue-500/10"
                                    : "text-slate-300 hover:text-blue-300 hover:bg-slate-800/60"
                         }`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="language-switcher"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border border-slate-700 hover:border-blue-500/60 hover:text-blue-300 transition-colors">
                  <Globe className="w-4 h-4" />
                  <span className="uppercase font-medium">{lang}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#0A1224] border-slate-800 text-slate-100">
                <DropdownMenuItem data-testid="language-switcher-en" onClick={() => setLanguage("en")}>English</DropdownMenuItem>
                <DropdownMenuItem data-testid="language-switcher-pt" onClick={() => setLanguage("pt")}>Português</DropdownMenuItem>
                <DropdownMenuItem data-testid="language-switcher-es" onClick={() => setLanguage("es")}>Español</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/join" data-testid="header-join-network"
                  className="hidden md:inline-flex btn-primary text-sm">
              {t.cta.join_network}
            </Link>

            <button className="lg:hidden p-2 rounded-md border border-slate-700"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="mobile-menu-toggle" aria-label="menu">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-800 bg-[#050914]">
            <div className="px-4 py-3 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
                         className={({ isActive }) =>
                           `px-3 py-2 text-sm rounded-md ${isActive ? "text-blue-300 bg-blue-500/10" : "text-slate-200"}`}>
                  {l.label}
                </NavLink>
              ))}
              <Link to="/join" onClick={() => setOpen(false)} className="mt-2 btn-primary text-sm text-center">
                {t.cta.join_network}
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Persistent professional disclaimer strip */}
      {loc.pathname !== "/disclaimer" && (
        <div className="disclaimer-strip">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-2.5 text-xs text-slate-300 flex flex-wrap items-center gap-2">
            <span className="font-semibold text-blue-300">MIFT</span>
            <span className="text-slate-400">·</span>
            <span>
              {lang === "en" && "Educational professional decision-support only. Not legal advice."}
              {lang === "pt" && "Apoio educacional à decisão profissional. Não constitui aconselhamento jurídico."}
              {lang === "es" && "Apoyo educativo a la decisión profesional. No constituye asesoría legal."}
            </span>
            <Link to="/disclaimer" data-testid="disclaimer-link"
                  className="ml-auto text-blue-300 hover:text-blue-200 underline underline-offset-4">
              {t.nav.disclaimer} →
            </Link>
          </div>
        </div>
      )}

      <main>{children}</main>

      <footer className="border-t border-slate-800 mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 grid gap-8 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-blue-500 rounded-full" />
              <span className="font-semibold">MIFT</span>
            </div>
            <p className="mt-3 text-sm text-slate-400 max-w-sm">{t.site.full_name}</p>
            <p className="mt-2 text-xs text-slate-500 max-w-sm">{t.site.tagline}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Link to="/framework" className="text-slate-300 hover:text-blue-300">{t.nav.framework}</Link>
            <Link to="/assessment" className="text-slate-300 hover:text-blue-300">{t.nav.assessment}</Link>
            <Link to="/toolkit" className="text-slate-300 hover:text-blue-300">{t.nav.toolkit}</Link>
            <Link to="/pilot" className="text-slate-300 hover:text-blue-300">{t.nav.pilot}</Link>
            <Link to="/feedback" className="text-slate-300 hover:text-blue-300">{t.nav.feedback}</Link>
            <Link to="/research" className="text-slate-300 hover:text-blue-300">{t.nav.research}</Link>
            <Link to="/book" className="text-slate-300 hover:text-blue-300">{t.nav.book}</Link>
            <Link to="/privacy" className="text-slate-300 hover:text-blue-300">{t.footer.privacy}</Link>
            <Link to="/disclaimer" className="text-slate-300 hover:text-blue-300">{t.nav.disclaimer}</Link>
          </div>
          <div className="text-sm text-slate-400">
            <p>{t.footer.copyright}</p>
            <p className="mt-2 text-xs text-slate-500">{t.footer.pre_launch}</p>
            <Link to="/admin" className="mt-3 inline-block text-xs text-slate-500 hover:text-slate-300">
              {t.footer.dev_log}
            </Link>
          </div>
        </div>

        {/* Full professional disclaimer visible on every page */}
        <div className="border-t border-slate-800/70 bg-[#050914]" data-testid="footer-full-disclaimer">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6">
            <p className="text-xs text-slate-500 leading-relaxed">{t.footer.full_disclaimer}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
